import { motion } from "framer-motion";

const focusAreas = [
  {
    title: "Networking & Infrastructure",
    desc: "Enterprise LAN/WAN design, ISP core networks, and structured cabling built for reliability and scale.",
  },
  {
    title: "Security Systems",
    desc: "Cybersecurity audits, SIEM deployment, IDS/IPS, CCTV surveillance, and access control systems.",
  },
  {
    title: "Software Engineering",
    desc: "Full-stack web development, automation tooling, API design, and cloud-native application deployment.",
  },
];

const milestones = [
  { year: "2020", event: "Carium Tech founded — focused on network engineering" },
  { year: "2021", event: "Expanded into cybersecurity & CCTV systems" },
  { year: "2022", event: "Delivered first ISP-scale network design (5,000+ subscribers)" },
  { year: "2023", event: "Launched software development division (React / Node.js)" },
  { year: "2024", event: "Achieved 50+ enterprise projects delivered across East Africa" },
  { year: "2025", event: "Built SOC monitoring platform for fintech clients" },
];

export default function About() {
  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6 },
  };

  return (
    <section className="min-h-screen pt-28 pb-20 px-6 lg:px-20 max-w-7xl mx-auto">
      {/* Mission */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-20"
      >
        <h1 className="text-5xl md:text-6xl font-bold mb-8">
          About{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-tech-green">
            Carium Tech
          </span>
        </h1>
        <div className="glass rounded-2xl p-8 md:p-12 max-w-4xl">
          <p className="text-xl md:text-2xl text-white font-semibold leading-relaxed">
            "Building secure, scalable, and intelligent digital systems for
            modern businesses."
          </p>
          <p className="text-gray-400 mt-6 text-lg leading-relaxed">
            Carium Tech is a technology agency specializing in cybersecurity,
            network infrastructure, CCTV deployment, ISP solutions, and software
            engineering. We partner with businesses across East Africa to design
            and implement systems that are secure by default, built to scale,
            and engineered for real-world performance.
          </p>
        </div>
      </motion.div>

      {/* Focus Areas */}
      <motion.div {...fadeUp} className="mb-20">
        <h2 className="text-3xl font-bold mb-8">
          Core{" "}
          <span className="text-cyber-blue">Focus Areas</span>
        </h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {focusAreas.map((area) => (
            <div
              key={area.title}
              className="glass rounded-2xl p-8 glass-hover"
            >
              <h3 className="text-xl font-bold text-tech-green mb-3">
                {area.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">{area.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Timeline */}
      <motion.div {...fadeUp}>
        <h2 className="text-3xl font-bold mb-8">
          Our{" "}
          <span className="text-tech-green">Journey</span>
        </h2>
        <div className="relative border-l border-cyber-blue/30 ml-4 space-y-10">
          {milestones.map((m, i) => (
            <motion.div
              key={m.year}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="ml-8 relative"
            >
              <div className="absolute -left-10 top-1 w-4 h-4 rounded-full bg-cyber-blue shadow-lg shadow-cyber-blue/50" />
              <span className="text-sm text-cyber-blue font-bold">
                {m.year}
              </span>
              <p className="text-gray-300 mt-1">{m.event}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
